import React from 'react';
import { i18n } from 'config/locales';
import PropTypes from 'prop-types';
import ValidationElement from '../ValidationElement';
import Field from '../Field';
import Show from '../Show';
import Text from '../Text';
import Checkbox from '../Checkbox';
import SelectDropdown from '../SelectDropdown';
import { setAttribute } from '../shared/setAttribute';

const propTypes = {
  first: PropTypes.string,
  firstLettersOnly: PropTypes.bool,
  last: PropTypes.string,
  middle: PropTypes.string,
  middleLettersOnly: PropTypes.bool,
  firstUnknown: PropTypes.bool,
  middleUnknown: PropTypes.bool,
  lastUnknown: PropTypes.bool,
  noFirstName: PropTypes.bool,
  showFirstUnknown: PropTypes.bool,
  showMiddleUnknown: PropTypes.bool,
  showLastUnknown: PropTypes.bool,
  hideNoFirstName: PropTypes.bool,
  hideNoMiddleName: PropTypes.bool,
  hideSuffix: PropTypes.bool,
  showLastFirstMiddleNameOrder: PropTypes.bool,
  noMiddleName: PropTypes.bool,
  hideMiddleName: PropTypes.bool,
  hideFirstLettersOnly: PropTypes.bool,
  hideMiddleLettersOnly: PropTypes.bool,
  hideLastLettersOnly: PropTypes.bool,
  suffix: PropTypes.string,
  suffixOther: PropTypes.string,
  prefix: PropTypes.string,
  focus: PropTypes.bool,
  error: PropTypes.bool,
  valid: PropTypes.bool,
  errorCodes: PropTypes.array,
  onUpdate: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  required: PropTypes.bool,
  attribute: PropTypes.string,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  title: PropTypes.string,
  scrollIntoView: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  className: PropTypes.string,
  isAliasOrNickname: PropTypes.bool,
};

export default class Name extends ValidationElement {
  constructor(props) {
    super(props);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      first: this.props.first,
      middle: this.props.middle,
    };

    this.first = React.createRef();
    this.firstUnknown = React.createRef();
    this.firstLettersOnly = React.createRef();
    this.middle = React.createRef();
    this.middleUnknown = React.createRef();
    this.noFirstName = React.createRef();
    this.noMiddleName = React.createRef();
    this.middleLettersOnly = React.createRef();
    this.lastUnknown = React.createRef();
    this.suffixRef = React.createRef();
  }

  // Removes non-letter characters on blur if somehow added, such as by copy/paste
  onlyAllowedCharacters = (value = '') => {
    let result = value;
    if (value) {
      result = value.replace(/[^a-zA-Z\-.' ]/g, '');
    }
    return result;
  };

  lettersOnlyOnKeyPress = (e) => {
    const code = e.keyCode ? e.keyCode : e.which; // permit letters and navigation
    const backspace = 8;
    const tabKey = 9;
    const deleteKey = 46;
    if (!((code >= 65 && code <= 90) // Letters
      || (code >= 35 && code <= 40) // Arrow Keys Home and End
      || (code === backspace)
      || (code === tabKey)
      || (code === deleteKey)
    )) {
      e.preventDefault();
    }
  };

  update = (queue, callback) => {
    // TODO: look at this setState + onUpdate occuring here and see if it can be cleaned up
    this.setState(
      {
        first: this.props.first.trim(),
        firstLettersOnly: this.props.firstLettersOnly,
        firstUnknown: this.props.firstUnknown,
        middleUnknown: this.props.middleUnknown,
        lastUnknown: this.props.lastUnknown,
        noFirstName: this.props.noFirstName,
        showFirstUnknown: this.props.showFirstUnknown,
        showMiddleUnknown: this.props.showMiddleUnknown,
        showLastUnknown: this.props.showLastUnknown,
        hideNoFirstName: this.props.hideNoFirstName,
        hideNoMiddleName: this.props.hideNoMiddleName,
        hideSuffix: this.props.hideSuffix,
        showLastFirstMiddleNameOrder: this.props.showLastFirstMiddleNameOrder,
        middle: this.props.middle.trim(),
        middleLettersOnly: this.props.middleLettersOnly,
        hideMiddleName: this.props.hideMiddleName,
        noMiddleName: this.props.noMiddleName,
        hideFirstLettersOnly: this.props.hideFirstLettersOnly,
        hideMiddleLettersOnly: this.props.hideMiddleLettersOnly,
        hideLastLettersOnly: this.props.hideLastLettersOnly,
        last: this.props.last.trim(),
        lastLettersOnly: this.props.lastLettersOnly,
        suffix: this.props.suffix,
        suffixOther: this.props.suffixOther.trim(),
        isAliasOrNickname: this.props.isAliasOrNickname,
      },
      () => {
        this.props.onUpdate({
          first: this.props.first.trim(),
          firstLettersOnly: this.props.firstLettersOnly,
          firstUnknown: this.props.firstUnknown,
          middleUnknown: this.props.middleUnknown,
          lastUnknown: this.props.lastUnknown,
          noFirstName: this.props.noFirstName,
          showFirstUnknown: this.props.showFirstUnknown,
          showMiddleUnknown: this.props.showMiddleUnknown,
          showLastUnknown: this.props.showLastUnknown,
          hideNoFirstName: this.props.hideNoFirstName,
          hideNoMiddleName: this.props.hideNoMiddleName,
          hideSuffix: this.props.hideSuffix,
          showLastFirstMiddleNameOrder: this.props.showLastFirstMiddleNameOrder,
          middle: this.props.middle.trim(),
          middleLettersOnly: this.props.middleLettersOnly,
          hideMiddleName: this.props.hideMiddleName,
          noMiddleName: this.props.noMiddleName,
          hideFirstLettersOnly: this.props.hideFirstLettersOnly,
          hideMiddleLettersOnly: this.props.hideMiddleLettersOnly,
          hideLastLettersOnly: this.props.hideLastLettersOnly,
          last: this.props.last.trim(),
          lastLettersOnly: this.props.lastLettersOnly,
          suffix: this.props.suffix,
          suffixOther: this.props.suffixOther.trim(),
          isAliasOrNickname: this.props.isAliasOrNickname,
          ...queue,
        });
      }
    );

    if (callback) {
      callback();
    }
  };

  updateIsAliasOrNickname = (/** @type {{ checked: Boolean; }} */ values) => {
    this.update({
      isAliasOrNickname: values?.checked,
      noFirstName: values?.checked ? false : this.props.noFirstName,
    });
  };

  updateFirstLetters = (values) => {
    this.update({
      firstUnknown: false,
      noFirstName: false,
      firstLettersOnly: values.checked,
    });
  };

  updateMiddleLetters = (values) => {
    this.update({
      middleUnknown: false,
      noMiddleName: false,
      middleLettersOnly: values.checked,
    });
  };

  updateLastLetters = (values) => {
    this.update({
      lastLettersOnly: values.checked,
    });
  };

  updateFirstNone = (values) => {
    let first = '';
    if (!values.checked) { first = this.state.first; }
    this.update({
      firstLettersOnly: false,
      first,
      firstUnknown: false,
      noFirstName: values.checked,
    });
  };

  updateFirstUnknown = (values) => {
    let first = '';
    if (!values.checked) { first = this.state.first; }
    this.update({
      firstLettersOnly: false,
      first,
      firstUnknown: values.checked,
      noFirstName: false,
    });
  };

  updateMiddleNone = (values) => {
    let middle = '';
    if (!values.checked) { middle = this.state.middle; }
    this.update({
      middleLettersOnly: false,
      middle,
      middleUnknown: false,
      noMiddleName: values.checked,
    });
  };

  updateMiddleUnknown = (values) => {
    let middle = '';
    if (!values.checked) { middle = this.state.middle; }
    this.update({
      middleLettersOnly: false,
      middle,
      middleUnknown: values.checked,
      noMiddleName: false,
    });
  };

  updateLastUnknown = (values) => {
    let last = '';
    if (!values.checked) { last = this.state.last; }
    this.update({
      lastLettersOnly: false,
      last,
      lastUnknown: values.checked,
    });
  };

  handleErrorFirst = (value, arr) => this.handleError('first', value, arr);

  handleErrorMiddle = (value, arr) => this.handleError('middle', value, arr);

  handleErrorLast = (value, arr) => this.handleError('last', value, arr);

  handleErrorSuffix = (value, arr) => this.handleError('suffix', value, arr);

  handleError = (code, value, arr) => {
    let errors = arr.map((err) => ({
      code: `name.${code}.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    errors = errors.concat(
      this.constructor.errors.map((err) => ({
        code: `name.${err.code}`,
        valid: err.func(value, { ...this.props, ...this.state }),
        uid: this.state.uid,
      }))
    );

    // Take the original and concatenate our new error values to it
    return this.props.onError(value, errors);
  };

  getSuffixOptions = () => {
    const { prefix } = this.props;
    return ['', 'jr', 'sr', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'other', 'none', 'unknown'].map((item) => {
      const text = item ? i18n.t(`${prefix}.pvq.label.${item}`) : item;
      return { label: text, value: text };
    });
  };

  filterErrors = (errors) => errors.filter((err) => err.code.indexOf('required') === -1);

  renderFirstName(prefix, textPrefix, maxFirst) {
    return (
      <Field
        title={i18n.t(`${prefix}.pvq.label.first`)}
        titleSize="label"
        help={`identification.pvq.${textPrefix}.first.help`}
        errorPrefix="name"
        className="usa-form-control"
        filterErrors={this.filterErrors}
        scrollIntoView={this.props.scrollIntoView}
        adjustFor="labels"
        attribute={setAttribute(this.props.attribute, 'first')}
        exactAttribute={this.props.exactAttribute}
      >
        <Text
          name="first"
          ariaLabel="First Name"
          ref={this.first}
          pattern="^[a-zA-Z\-\.' ]*$"
          minlength={this.props.firstLettersOnly ? 1 : 2}
          maxlength={maxFirst}
          additionalLengthErrors
          className="first"
          value={this.props.first}
          attribute={setAttribute(this.props.attribute, 'first')}
          onUpdate={(values) => {
            this.update({
              first: values.value.trim(),
            });
          }}
          onError={this.handleErrorFirst}
          onFocus={this.props.onFocus}
          required={!this.props.noFirstName && this.props.required && !this.props.firstUnknown}
          disabled={((this.props.noFirstName || this.props.firstUnknown) && this.props.first?.length === 0) || this.props.disabled}
        />
        <div className="flags">
          {this.props.showFirstUnknown && (
          <Checkbox
            name="firstUnknown"
            ariaLabel="I Don't Know"
            ref={this.firstUnknown}
            label={i18n.t(`${prefix}.pvq.label.unknown`)}
            className="first-unknown"
            toggle="false"
            value={this.props.firstUnknown}
            checked={this.props.firstUnknown}
            onUpdate={this.updateFirstUnknown}
            onError={this.handleErrorFirst}
            disabled={this.props.disabled}
          />
          )}
          {!this.props.hideNoFirstName && !this.props.isAliasOrNickname && (
          <Checkbox
            name="noFirstName"
            ariaLabel="No First Name"
            ref={this.noFirstName}
            label={i18n.t(`${prefix}.pvq.label.noFirst`)}
            className="first-none"
            toggle="false"
            value={this.props.noFirstName}
            checked={this.props.noFirstName}
            onUpdate={this.updateFirstNone}
            onError={this.handleErrorFirst}
            disabled={this.props.disabled}
          />
          )}
          {!this.props.hideFirstLettersOnly && (
          <Checkbox
            name="firstLettersOnly"
            ariaLabel="First Letter(s) Only"
            ref={this.firstLettersOnly}
            label={i18n.t(`${prefix}.pvq.label.lettersOnly`)}
            className="first-letters-only"
            toggle="false"
            value={this.props.firstLettersOnly}
            checked={this.props.firstLettersOnly}
            onUpdate={this.updateFirstLetters}
            onError={this.handleErrorFirst}
            disabled={this.props.disabled}
          />
          )}
        </div>
      </Field>
    );
  }

  renderMiddleName(prefix, textPrefix, maxMiddle) {
    return (
      <Field

        title={i18n.t(`${prefix}.pvq.label.middle`)}
        titleSize="label"
        help={`identification.pvq.${textPrefix}.middle.help`}
        errorPrefix="name"
        className="usa-form-control"
        filterErrors={this.filterErrors}
        scrollIntoView={this.props.scrollIntoView}
        adjustFor="labels"
        attribute={setAttribute(this.props.attribute, 'middle')}
        exactAttribute={this.props.exactAttribute}
      >
        <Text
          name="middle"
          ariaLabel="Middle Name"
          ref={this.middle}
          pattern="^[a-zA-Z\-\.' ]*$"
          minlength={this.props.middleLettersOnly ? 1 : 2}
          maxlength={maxMiddle}
          additionalLengthErrors
          className="middle"
          value={this.props.middle}
          disabled={
                this.props.disabled
                || this.props.middle?.length === 0
                  && (this.props.middleUnknown || this.props.noMiddleName)
              }
          onUpdate={(values) => {
            this.update({
              middle: values.value.trim(),
            });
          }}
          onError={this.handleErrorMiddle}
          onFocus={this.props.onFocus}
          required={this.props.required && !this.props.middleUnknown && !this.props.noMiddleName}
          attribute={setAttribute(this.props.attribute, 'middle')}
        />

        <div className="middle-options flags">
          {this.props.showMiddleUnknown && (
          <Checkbox
            name="middleUnknown"
            ariaLabel="I Don't Know"
            ref={this.middleUnknown}
            label={i18n.t(`${prefix}.pvq.label.middleUnknown`)}
            className="middle-unknown"
            toggle="false"
            value={this.props.middleUnknown}
            checked={this.props.middleUnknown}
            onUpdate={this.updateMiddleUnknown}
            onError={this.handleErrorMiddle}
            disabled={this.props.disabled}
          />
          )}
          {!this.props.hideNoMiddleName
              && (
              <Checkbox
                name="noMiddleName"
                ariaLabel="No Middle Name"
                ref={this.noMiddleName}
                label={i18n.t(`${prefix}.pvq.label.noMiddle`)}
                className="middle-none"
                toggle="false"
                value={this.props.noMiddleName}
                checked={this.props.noMiddleName}
                onUpdate={this.updateMiddleNone}
                onError={this.handleErrorMiddle}
                disabled={this.props.disabled}
              />
              )}
          {!this.props.hideMiddleLettersOnly && (
          <Checkbox
            name="middleLettersOnly"
            ariaLabel="Middle Letter(s) Only"
            ref={this.middleLettersOnly}
            label={i18n.t(`${prefix}.pvq.label.lettersOnly`)}
            className="middle-letters-only"
            toggle="false"
            value={this.props.middleLettersOnly}
            checked={this.props.middleLettersOnly}
            onUpdate={this.updateMiddleLetters}
            onError={this.handleErrorMiddle}
            disabled={this.props.disabled}
          />
          )}
        </div>
      </Field>
    );
  }

  renderLastName(prefix, textPrefix, maxLast) {
    return (
      <Field
        title={i18n.t(`${prefix}.pvq.label.last`)}
        titleSize="label"
        errorPrefix="name"
        help={`identification.pvq.${textPrefix}.last.help`}
        className="usa-form-control"
        filterErrors={this.filterErrors}
        scrollIntoView={this.props.scrollIntoView}
        adjustFor="labels"
        attribute={setAttribute(this.props.attribute, 'last')}
        exactAttribute={this.props.exactAttribute}
      >
        <Text
          name="last"
          ariaLabel="Last Name"
          minlength={1}
          maxlength={maxLast}
          className="last"
          pattern="^[a-zA-Z\-\.' ]*$"
          value={this.props.last}
          onUpdate={(values) => {
            this.update({
              last: values.value.trim(),
            });
          }}
          onError={this.handleErrorLast}
          onFocus={this.props.onFocus}
          required={this.props.required && !this.props.lastUnknown}
          disabled={this.props.disabled || (this.props.last?.length === 0 && this.props.lastUnknown)}
          attribute={setAttribute(this.props.attribute, 'last')}
        />
        <div className="last-options flags">
          {this.props.showLastUnknown && (
          <Checkbox
            name="lastUnknown"
            ariaLabel="I Don't Know"
            ref={this.lastUnknown}
            label={i18n.t(`${prefix}.pvq.label.unknown`)}
            className="last-unknown"
            toggle="false"
            value={this.props.lastUnknown}
            checked={this.props.lastUnknown}
            onUpdate={this.updateLastUnknown}
            onError={this.handleErrorLast}
            disabled={this.props.disabled}
          />
          )}
          {!this.props.hideLastLettersOnly && (
          <Checkbox
            name="lastLettersOnly"
            ariaLabel="Last Letter(s) Only"
            ref={this.lastLettersOnly}
            label={i18n.t(`${prefix}.pvq.label.lettersOnly`)}
            className="last-letters-only"
            toggle="false"
            value={this.props.lastLettersOnly}
            checked={this.props.lastLettersOnly}
            onUpdate={this.updateLastLetters}
            onError={this.handleErrorMiddle}
            disabled={this.props.disabled}
          />
          )}
        </div>
      </Field>
    );
  }

  renderSuffix(prefix, textPrefix) {
    return (
      <><div style={{ height: '50px' }} /><Field
        title={i18n.t(`${prefix}.pvq.label.suffix`)}
        titleSize="label"
        help={`identification.pvq.${textPrefix}.suffix.help`}
        errorPrefix="name"
        className="usa-form-control"
        filterErrors={this.filterErrors}
        scrollIntoView={this.props.scrollIntoView}
        optional
        optionalText={i18n.t(`${prefix}.pvq.label.optional`)}
        labelFor={`selectDropdown-${this.state.uid}`}
        attribute={setAttribute(this.props.attribute, 'suffix')}
      >
        <SelectDropdown
          className="option-list suffix usa-small-input"
          isDisabled={this.props.disabled}
          ref={this.suffixRef}
          name="suffix"
          ariaLabel="Name Suffix"
          onChange={(e) => {
            this.update({
              suffix: e.target.value,
              suffixOther: e.target.value !== `I Don't Know` && e.target.value !== 'Other' ? '' : this.props.suffixOther,
            });
          }}
          onError={this.handleErrorSuffix}
          value={this.props.suffix || ''}
          required={false}
          labelKey={`selectDropdown-${this.state.uid}`}
          attribute={setAttribute(this.props.attribute, 'suffix')}
          id={`selectDropdown-${this.state.uid}`}
        >
          {this.getSuffixOptions().map((option) => (
            <option key={`name-${option.label}`} label={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectDropdown>
        <Show when={(this.props.suffix === 'Other' || this.props.suffix === `I Don't Know`)}>
          <Text
            attribute={setAttribute(this.props.attribute, 'suffixOther')}
            name="suffixOther"
            ariaLabel={`${this.props.suffix === `I Don't Know` ? 'Unknown' : 'Other'} Suffix`}
            label={i18n.t(`${prefix}.pvq.description.${this.props.suffix === `I Don't Know` ? 'unknown' : 'other'}`)}
            maxlength="100"
            className="suffix-other"
            value={this.props.suffixOther}
            onUpdate={(values) => {
              this.update({
                suffixOther: values.value.trim(),
              });
            }}
            onError={this.handleErrorSuffix}
            onFocus={this.props.onFocus}
            required={this.props.required && (this.props.suffix === 'Other' || this.props.suffix === `I Don't Know`)}
            disabled={this.props.disabled}
          />
        </Show>
      </Field>
      </>
    );
  }

  render() {
    const { prefix, textPrefix } = this.props;
    const klass = [
      `name ${this.props.className || ''}`.trim(),
      this.props.disabled ? 'disabled' : '',
    ];
    const maxFirst = '100';
    const maxMiddle = '100';
    const maxLast = '100';

    return (
      <div className={klass.join(' ')}>
        {this.props.title && <h4 className="title h4">{this.props.title}</h4>}
        {this.props.showCheckboxAliasOrNickname && (
          <Checkbox
            label={i18n.t(`${prefix}.pvq.label.aliasornickname`)}
            checked={this.props.isAliasOrNickname}
            attribute={`${this.props.attribute}.IsAliasOrNickname`}
            name="isAliasOrNickname"
            className="alias-nickname"
            value={this.props.isAliasOrNickname}
            onUpdate={this.updateIsAliasOrNickname}
            onError={this.props.onError}
            disabled={this.props.disabled}
          />
        )}
        {this.props.showLastFirstMiddleNameOrder
          ? (
            <>
              {this.renderLastName(prefix, textPrefix, maxLast)}
              {this.renderFirstName(prefix, textPrefix, maxFirst)}
              {!this.props.hideMiddleName && (this.renderMiddleName(prefix, textPrefix, maxMiddle))}
              {!this.props.hideSuffix && (this.renderSuffix(prefix, textPrefix))}
            </>
          ) : (
            <>
              {this.renderFirstName(prefix, textPrefix, maxFirst)}
              {!this.props.hideMiddleName && (this.renderMiddleName(prefix, textPrefix, maxMiddle))}
              {this.renderLastName(prefix, textPrefix, maxLast)}
              {!this.props.hideSuffix && (this.renderSuffix(prefix, textPrefix))}
            </>
          )}
      </div>
    );
  }
}

Name.propTypes = propTypes;

Name.defaultProps = {
  showCheckboxAliasOrNickname: false,
  first: '',
  firstLettersOnly: false,
  last: '',
  middle: '',
  middleLettersOnly: false,
  firstUnknown: false,
  middleUnknown: false,
  lastUnknown: false,
  noFirstName: false,
  showFirstUnknown: false,
  showMiddleUnknown: false,
  showLastUnknown: false,
  hideNoFirstName: false,
  hideNoMiddleName: false,
  noMiddleName: false,
  hideMiddleName: false,
  hideFirstLettersOnly: false,
  hideMiddleLettersOnly: false,
  hideLastLettersOnly: false,
  hideSuffix: false,
  isAliasOrNickname: false,
  showLastFirstMiddleNameOrder: false,
  exactAttribute: false,
  suffix: '',
  suffixOther: '',
  prefix: 'name',
  textPrefix: 'applicantName',
  focus: false,
  error: false,
  valid: false,
  required: false,
  errorCodes: [],
  onUpdate: () => { },
  onError: (value, arr) => arr,
};

Name.requiredErrorsOnly = (errors) => errors.filter((err) => err.code.indexOf('required') !== -1);

Name.errors = [];
